import React, { useEffect, useState } from 'react'
import { useRouteMatch, useLocation, matchPath } from 'react-router-dom'
import { createTheme, darken, lighten, ThemeProvider } from '@material-ui/core'

import useFetch from 'hooks/useFetch'

import ClientPortalThemeContext from 'contexts/ClientPortalThemeContext'

import defaultTheme from 'theme'
import * as service from 'service'
import constants from 'constants/index'
import { ORGANIZATION_SLUG } from 'service/constants'

const ClientPortalThemeProvider = ({ children }) => {
  const { DEFAULT_SLUGS_PATHS } = constants.settings

  const [theme, setTheme] = useState(defaultTheme)
  const [loading, setLoading] = useState(true)

  const routeMatch = useRouteMatch()
  const location = useLocation()

  const routePublicPath = routeMatch?.params?.slug
  const storedPublicPath = sessionStorage.getItem(ORGANIZATION_SLUG)

  // Nas rotas de fluxo SSO/Microsoft o slug da URL é não-confiável: o backend
  // força 'dponet' no retorno do SSO e o MSAL volta sem slug. Nesses casos
  // priorizamos o slug salvo e NÃO mexemos no storage, para o white-label
  // sobreviver às telas de loading/erro.
  // Padrões espelham as rotas em Routes.js (não importamos daqui para evitar
  // dependência circular Routes → provider → Routes).
  const AUTH_FLOW_PATHS = ['/:slug/sso', '/:slug/sso/error', '/login/microsoft']
  const isAuthFlowRoute = AUTH_FLOW_PATHS.some((path) =>
    matchPath(location.pathname, { path, exact: true }),
  )

  const publicPath = isAuthFlowRoute
    ? storedPublicPath || routePublicPath
    : routePublicPath || storedPublicPath

  const isRootWithoutSlug = location.pathname === '/'

  const isDefaultSlug = DEFAULT_SLUGS_PATHS.includes(publicPath?.toLowerCase())

  const isRealSlug = publicPath && !isDefaultSlug

  const shouldLoadCustomTheme = !!isRealSlug && !isRootWithoutSlug

  const { response, isLoadingStart } = useFetch(
    service.dponet.whiteLabel.getPublicTheme,
    {
      publicPath,
    },
    [publicPath],
    shouldLoadCustomTheme,
  )

  const clientPortalTheme = response?.data?.clientPortalTheme

  useEffect(() => {
    if (clientPortalTheme) {
      setLoading(true)

      let dynamicTheme = createTheme()

      const primaryColor =
        clientPortalTheme?.primaryColor || defaultTheme.palette.primary.main
      const secondaryColor =
        clientPortalTheme?.secondaryColor || defaultTheme.palette.secondary.main

      dynamicTheme = createTheme(defaultTheme, {
        palette: {
          primary: {
            main: primaryColor,
            light: lighten(primaryColor, 0.9),
            dark: darken(primaryColor, 0.3),
            hover: darken(primaryColor, 0.2),
            contrastText: clientPortalTheme.primaryFontColor,
          },
          secondary: {
            main: secondaryColor,
            light: lighten(secondaryColor, 0.9),
            dark: darken(primaryColor, 0.3),
            hover: darken(secondaryColor, 0.2),
            contrastText: clientPortalTheme.primaryFontColor,
          },
          text: {
            primary: defaultTheme.palette.common.black,
          },
        },
      })

      setTheme(dynamicTheme)
    }

    if (!isAuthFlowRoute) {
      if (isDefaultSlug || isRootWithoutSlug)
        sessionStorage.removeItem(ORGANIZATION_SLUG)
      else if (publicPath) sessionStorage.setItem(ORGANIZATION_SLUG, publicPath)
    }

    if (!isLoadingStart || !shouldLoadCustomTheme) setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPortalTheme, isLoadingStart])

  return (
    <ClientPortalThemeContext.Provider
      value={{
        clientPortalTheme: clientPortalTheme,
        isLoading: loading,
      }}
    >
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ClientPortalThemeContext.Provider>
  )
}

export default ClientPortalThemeProvider
